/**
 * useMobileOverlay — single hook centralising the overlay invariants
 * required by `/app/memory/MOBILE_SHELL_CONTRACT.md` §2:
 *
 *   • body-scroll-lock while open (reuses useScrollLock)
 *   • ESC dismiss
 *   • focus restoration to the trigger element on close
 *   • single onClose path so every caller has identical semantics
 *
 * Used internally by MobileDialog, MobileBottomSheet, MobileMenu,
 * MobilePopover. Do NOT call directly from page components — that's
 * a Contract violation (use the primitives instead).
 *
 * Phase 1 — Mobile Shell programme. May 14, 2026.
 */
import { useCallback, useEffect, useRef } from "react";
import { useScrollLock } from "./useScrollLock";

export function useMobileOverlay({
  open,
  onClose,
  dismissibleOnEsc = true,
  lockBodyScroll = true,
  restoreFocus = true,
} = {}) {
  const triggerRef = useRef(null);

  // Capture the element that had focus when the overlay opened so
  // we can restore it on close.
  useEffect(() => {
    if (open && restoreFocus) {
      triggerRef.current = document.activeElement;
    }
    if (!open && triggerRef.current && restoreFocus) {
      // Wait one frame so the DOM unmount completes before restoring.
      const el = triggerRef.current;
      requestAnimationFrame(() => {
        try { el?.focus?.({ preventScroll: true }); } catch { /* noop */ }
      });
      triggerRef.current = null;
    }
  }, [open, restoreFocus]);

  // ESC dismiss.
  useEffect(() => {
    if (!open || !dismissibleOnEsc) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, dismissibleOnEsc, onClose]);

  // Body scroll lock.
  useScrollLock(open && lockBodyScroll);

  // Tap-outside helper for backdrop click handlers — when the caller
  // wires this to the backdrop AND uses stopPropagation on the
  // content node, clicks on content stop at content and never bubble
  // up; clicks on the backdrop itself bubble up here and dismiss.
  // We intentionally do NOT rely on `e.target === e.currentTarget`
  // because the visual-edits AST wrapping makes that comparison
  // brittle in dev builds.
  const onBackdropClick = useCallback(() => {
    onClose?.();
  }, [onClose]);

  return { onBackdropClick };
}

export default useMobileOverlay;
